import React from 'react';
import { Card, Tag, Row, Col, Progress, Tooltip, Space, Statistic } from 'antd';
import { ThunderboltOutlined, WarningOutlined } from '@ant-design/icons';
import { Scale, BicepsFlexed } from 'lucide-react';
import { useIntl } from 'react-intl';

export interface StatementData {
    id: string;
    text: string;
    responseCount: number;
    meanScore?: number | null | undefined;
    polarityPercentage?: number | null | undefined;
    intensityPercentage?: number | null | undefined;
    brandPowerPercentage?: number | null | undefined;
    controversyPercentage?: number | null | undefined;
    scoreDistribution?: any;
    questionTypeId?: number;
    scoringType?: string;
    polarityValue?: number | null | undefined;
    intensityValue?: number | null | undefined;
    brandPowerValue?: number | null | undefined;
    controversyValue?: number | null | undefined;
}

interface StatementCardProps {
    statement: StatementData;
    isAdmin?: boolean;
    onResponseCountClick?: (statement: StatementData) => void;
    theme?: 'dark' | 'light';
}

export const StatementCard: React.FunctionComponent<StatementCardProps> = ({
    statement,
    isAdmin = false,
    onResponseCountClick,
    theme = 'light'
}) => {
    const intl = useIntl();
    const isDark = theme === 'dark';

    const getMetricColor = (percentage: number, type: 'polarity' | 'intensity' | 'power' | 'controversy') => {
        if (type === 'controversy') {
            // Controversy: lower is better
            if (percentage <= 20) return '#237804'; // Dark green
            if (percentage <= 40) return '#52c41a'; // Light green
            if (percentage <= 60) return '#faad14'; // Yellow
            if (percentage <= 80) return '#fa8c16'; // Orange
            return '#f5222d'; // Red for 81-100
        }
        
        // Standardized colors for Score, Intensity, and Brand Power
        if (percentage < 0) return '#000000'; // Black for negative values (only Score)
        if (percentage <= 20) return '#f5222d'; // Red
        if (percentage <= 40) return '#fa8c16'; // Orange
        if (percentage <= 60) return '#faad14'; // Yellow
        if (percentage <= 80) return '#52c41a'; // Light green
        return '#237804'; // Dark green for 81-100
    };
    
    const getPolarityColor = (percentage: number) => {
        // Use standardized color scheme for polarity
        if (percentage < 0) return '#000000'; // Black for negative values
        if (percentage === 0) return '#f5222d'; // Red for 0
        if (percentage <= 20) return '#f5222d'; // Red
        if (percentage <= 40) return '#fa8c16'; // Orange
        if (percentage <= 60) return '#faad14'; // Yellow
        if (percentage <= 80) return '#52c41a'; // Light green
        return '#237804'; // Dark green for 81-100
    };
    
    const getMetricIcon = (type: 'polarity' | 'intensity' | 'power' | 'controversy', color?: string, isNull?: boolean) => {
        const iconColor = isNull ? '#bfbfbf' : color;
        const iconStyle = { color: iconColor, fontSize: 'inherit' };
        switch (type) {
            case 'polarity': return <Scale size="1em" color={iconColor} />;
            case 'intensity': return <ThunderboltOutlined style={iconStyle} />;
            case 'power': return <BicepsFlexed size="1em" color={iconColor} />;
            case 'controversy': return <WarningOutlined style={iconStyle} />;
            default: return null;
        }
    };

    const handleResponseCountClick = () => {
        if (onResponseCountClick && statement.responseCount > 0) {
            onResponseCountClick(statement);
        }
    };

    return (
        <Card
            hoverable
            style={{
                height: '100%',
                position: 'relative',
                border: isDark ? '2px solid rgba(100, 116, 139, 0.3)' : '2px solid #d9d9d9',
                boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.09)',
                backgroundColor: isDark ? '#1e293b' : '#ffffff'
            }}
            styles={{ body: { padding: 20, backgroundColor: isDark ? '#1e293b' : '#ffffff' } }}
        >
            <div style={{
                marginBottom: 16,
                backgroundColor: isDark ? '#0f172a' : '#fafafa',
                margin: '-20px -20px 16px -20px',
                padding: '20px 20px 16px 20px',
                borderRadius: '8px 8px 0 0',
                borderBottom: isDark ? '1px solid rgba(100, 116, 139, 0.3)' : '1px solid #f0f0f0'
            }}>
                <Tag color="purple" style={{ position: 'absolute', top: 12, right: 12, fontSize: '11px', zIndex: 1 }}>
                    {statement.id}
                </Tag>
                <h4 style={{
                    fontSize: 16,
                    marginBottom: 8,
                    paddingRight: 60,
                    color: isDark ? '#f1f5f9' : '#262626',
                    fontWeight: 600,
                    height: '48px',
                    lineHeight: '24px',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                }}>
                    {statement.text}
                </h4>
                <div style={{ display: 'flex', gap: 8, marginBottom: 0 }}>
                    <Tag
                        color="geekblue"
                        style={{
                            cursor: (onResponseCountClick && statement.responseCount > 0) ? 'pointer' : 'default',
                            fontSize: '12px',
                            padding: '2px 8px'
                        }}
                        onClick={handleResponseCountClick}
                    >
                        Responses: {statement.responseCount}
                    </Tag>
                </div>
            </div>

            {isAdmin && (
                <div style={{ marginBottom: 20 }}>
                    <Statistic
                        title={<span style={{ color: isDark ? '#94a3b8' : undefined }}>Element Score</span>}
                        value={statement.meanScore != null ? statement.meanScore : '-'}
                        precision={1}
                        suffix={statement.meanScore != null ? '%' : ''}
                        valueStyle={{ color: statement.meanScore == null ? '#bfbfbf' : (isDark ? '#f1f5f9' : undefined) }}
                    />
                </div>
            )}

            <Row gutter={[16, 16]}>
                {statement.polarityPercentage !== undefined && (
                    <Col span={12}>
                        <Tooltip title={intl.formatMessage({ id: 'statements.score.tooltip' })}>
                            <div style={{ textAlign: 'center' }}>
                                <Space direction="vertical" size="small">
                                    <span style={{ fontSize: 18 }}>
                                        {getMetricIcon('polarity', statement.polarityPercentage == null ? '#bfbfbf' : 
                                            getPolarityColor(statement.polarityPercentage), statement.polarityPercentage == null)}
                                    </span>
                                    <div style={{ position: 'relative' }}>
                                        <Progress
                                            type="circle"
                                            percent={Math.abs(statement.polarityPercentage || 0)}
                                            size={80}
                                            strokeColor={statement.polarityPercentage == null ? '#bfbfbf' :
                                                getPolarityColor(statement.polarityPercentage)}
                                            trailColor={isDark ? 'rgba(100, 116, 139, 0.3)' : undefined}
                                            format={() => {
                                                if (statement.polarityPercentage == null) return '--';
                                                const value = Math.round(statement.polarityPercentage);
                                                const text = `${value >= 0 ? '+' : ''}${value}%`;
                                                const color = getPolarityColor(value);
                                                
                                                return (
                                                    <span style={{ color: color }}>
                                                        {text}
                                                    </span>
                                                );
                                            }}
                                        />
                                        {statement.polarityPercentage != null && statement.polarityPercentage < 0 && (
                                            <div style={{ 
                                                position: 'absolute', 
                                                top: -10, 
                                                left: '50%', 
                                                transform: 'translateX(-50%)',
                                                fontSize: 16,
                                                color: '#000000', // Black for negative values
                                                fontWeight: 'bold'
                                            }}>
                                                ↓
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ fontSize: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                        <span style={{ color: isDark ? '#e2e8f0' : undefined }}>{intl.formatMessage({ id: 'statements.score' })}</span>
                                        <span style={{ fontSize: 10, color: isDark ? '#64748b' : '#8c8c8c' }}>
                                            {intl.formatMessage({ id: 'statements.scale.bipolar' })}
                                        </span>
                                    </div>
                                </Space>
                            </div>
                        </Tooltip>
                    </Col>
                )}

                {statement.intensityPercentage !== undefined && (
                    <Col span={12}>
                        <Tooltip title={intl.formatMessage({ id: 'statements.intensity.tooltip' })}>
                            <div style={{ textAlign: 'center' }}>
                                <Space direction="vertical" size="small">
                                    <span style={{ fontSize: 18 }}>
                                        {getMetricIcon('intensity', getMetricColor(statement.intensityPercentage || 0, 'intensity'), statement.intensityPercentage == null)}
                                    </span>
                                    <Progress
                                        type="circle"
                                        percent={Math.round(statement.intensityPercentage || 0)}
                                        size={80}
                                        strokeColor={statement.intensityPercentage == null ? '#bfbfbf' : getMetricColor(statement.intensityPercentage, 'intensity')}
                                        trailColor={isDark ? 'rgba(100, 116, 139, 0.3)' : undefined}
                                        format={(percent) => {
                                            if (statement.intensityPercentage == null) return '--';
                                            const value = Math.round(percent || 0);
                                            const color = getMetricColor(statement.intensityPercentage, 'intensity');
                                            return (
                                                <span style={{ color: color }}>
                                                    {value}%
                                                </span>
                                            );
                                        }}
                                    />
                                    <div style={{ fontSize: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                        <span style={{ color: isDark ? '#e2e8f0' : undefined }}>{intl.formatMessage({ id: 'statements.intensity' })}</span>
                                        <span style={{ fontSize: 10, color: isDark ? '#64748b' : '#8c8c8c' }}>
                                            {intl.formatMessage({ id: 'statements.scale.positive' })}
                                        </span>
                                    </div>
                                </Space>
                            </div>
                        </Tooltip>
                    </Col>
                )}

                {statement.brandPowerPercentage !== undefined && (
                    <Col span={12}>
                        <Tooltip title={intl.formatMessage({ id: 'statements.brandPower.tooltip' })}>
                            <div style={{ textAlign: 'center' }}>
                                <Space direction="vertical" size="small">
                                    <span style={{ fontSize: 18 }}>
                                        {getMetricIcon('power', statement.brandPowerPercentage == null ? '#bfbfbf' : 
                                            getPolarityColor(statement.brandPowerPercentage), statement.brandPowerPercentage == null)}
                                    </span>
                                    <div style={{ position: 'relative' }}>
                                        <Progress
                                            type="circle"
                                            percent={Math.abs(statement.brandPowerPercentage || 0)}
                                            size={80}
                                            strokeColor={statement.brandPowerPercentage == null ? '#bfbfbf' :
                                                getPolarityColor(statement.brandPowerPercentage)}
                                            trailColor={isDark ? 'rgba(100, 116, 139, 0.3)' : undefined}
                                            format={() => {
                                                if (statement.brandPowerPercentage == null) return '--';
                                                const value = Math.round(statement.brandPowerPercentage);
                                                const text = `${value >= 0 ? '+' : ''}${value}%`;
                                                const color = getPolarityColor(value);
                                                
                                                return (
                                                    <span style={{ color: color }}>
                                                        {text}
                                                    </span>
                                                );
                                            }}
                                        />
                                        {statement.brandPowerPercentage != null && statement.brandPowerPercentage < 0 && (
                                            <div style={{ 
                                                position: 'absolute', 
                                                top: -10, 
                                                left: '50%', 
                                                transform: 'translateX(-50%)',
                                                fontSize: 16,
                                                color: '#000000', // Black for negative values
                                                fontWeight: 'bold'
                                            }}>
                                                ↓
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ fontSize: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                        <span style={{ color: isDark ? '#e2e8f0' : undefined }}>{intl.formatMessage({ id: 'statements.brandPower' })}</span>
                                        <span style={{ fontSize: 10, color: isDark ? '#64748b' : '#8c8c8c' }}>
                                            {intl.formatMessage({ id: 'statements.scale.bipolar' })}
                                        </span>
                                    </div>
                                </Space>
                            </div>
                        </Tooltip>
                    </Col>
                )}

                {statement.controversyPercentage !== undefined && (
                    <Col span={12}>
                        <Tooltip title={intl.formatMessage({ id: 'statements.controversy.tooltip' })}>
                            <div style={{ textAlign: 'center' }}>
                                <Space direction="vertical" size="small">
                                    <span style={{ fontSize: 18 }}>
                                        {getMetricIcon('controversy', getMetricColor(statement.controversyPercentage || 0, 'controversy'), statement.controversyPercentage == null)}
                                    </span>
                                    <Progress
                                        type="circle"
                                        percent={statement.controversyPercentage == null ? 0 : Math.round(Math.abs(statement.controversyPercentage))}
                                        size={80}
                                        strokeColor={statement.controversyPercentage == null ? '#bfbfbf' :
                                            statement.controversyPercentage >= 0 ? getMetricColor(Math.abs(statement.controversyPercentage), 'controversy') : '#ff4d4f'}
                                        trailColor={isDark ? 'rgba(100, 116, 139, 0.3)' : undefined}
                                        format={() => {
                                            if (statement.controversyPercentage == null) return '--';
                                            const value = Math.round(statement.controversyPercentage);
                                            const text = `${value}%`;
                                            const color = getMetricColor(Math.abs(statement.controversyPercentage), 'controversy');
                                            
                                            return (
                                                <span style={{ color: color }}>
                                                    {text}
                                                </span>
                                            );
                                        }}
                                    />
                                    <div style={{ fontSize: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                        <span style={{ color: isDark ? '#e2e8f0' : undefined }}>{intl.formatMessage({ id: 'statements.controversy' })}</span>
                                        <span style={{ fontSize: 10, color: isDark ? '#64748b' : '#8c8c8c' }}>
                                            {intl.formatMessage({ id: 'statements.scale.positive' })}
                                        </span>
                                    </div>
                                </Space>
                            </div>
                        </Tooltip>
                    </Col>
                )}
            </Row>
        </Card>
    );
};
